import { Grid, Box, InlineBanner } from '@cad-arc';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { CheckboxMultiselect } from '../../common/components/checkbox-multiselect';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { BaseSelect } from '../../forms/inputs/base-select';
import { queueFilterEnum } from '../queue-filter-enum';
import { queueFilterOptions } from '../queue-filter-options';
import type { AssignmentStatus, QueueFilter, QueueFilterName } from '../types';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

export type FormData = {
    dataEntityType:
        | 'UNIT'
        | 'CAD_AGENCY_EVENT'
        | 'TRU_EVENT'
        | 'SCHEDULED_AGENCY_EVENT'
        | 'HOSPITAL'
        | 'BOLO';
    filters: QueueFilterName[];
};

function getOptionsOutsideAssignmentStatus(assignmentStatus: AssignmentStatus): string[] {
    return Object.entries(queueFilterEnum)
        .filter(([, filter]) => filter.assignmentStatus !== assignmentStatus)
        .map((e) => e[0]);
}

function getHasAssignmentStatus(
    filters: QueueFilterName[],
    assignmentStatus: AssignmentStatus
): boolean {
    return filters
        .map((v: QueueFilterName) => queueFilterEnum[v])
        .some((filter: QueueFilter) => filter.assignmentStatus === assignmentStatus);
}

export function QueueCreationForm(props: { isError: boolean }): React.ReactElement {
    const [disabledOptions, setDisabledOptions] = useState<Array<string>>([]);
    const [isCheckboxDisabled, setIsCheckboxDisabled] = useState<boolean>(false);
    const { control, setValue } = useFormContext();
    const dataEntityTypeController = useController({
        name: 'dataEntityType',
        control,
    });
    const filtersController = useController({ name: 'filters', control });
    const { viewBolos, hospitalsEnabled } = useEnabledFeatures();

    // passing refs to our form fields results in a console warning from React
    const { ref: throwawayRef1, ...dataEntityTypeField } = dataEntityTypeController.field;
    const { ref: throwawayRef2, ...filtersField } = filtersController.field;

    const filters = filtersField.value;
    const dataEntityType = dataEntityTypeField.value;

    useEffect(() => {
        if (
            dataEntityType === 'TRU_EVENT' ||
            dataEntityType === 'SCHEDULED_AGENCY_EVENT' ||
            dataEntityType === 'HOSPITAL' ||
            dataEntityType === 'BOLO'
        ) {
            setIsCheckboxDisabled(true);
            setValue('filters', []);
        } else {
            setIsCheckboxDisabled(false);
            setValue('filters', []);
        }
    }, [dataEntityType, setValue]);

    useEffect(() => {
        if (getHasAssignmentStatus(filters, 'ALL')) {
            setDisabledOptions(getOptionsOutsideAssignmentStatus('ALL'));
        } else if (getHasAssignmentStatus(filters, 'ASSIGNED')) {
            setDisabledOptions(getOptionsOutsideAssignmentStatus('ASSIGNED'));
        } else if (getHasAssignmentStatus(filters, 'UNASSIGNED')) {
            setDisabledOptions(getOptionsOutsideAssignmentStatus('UNASSIGNED'));
        } else if (filters.length === 0) {
            setDisabledOptions([]);
        }
    }, [filters]);

    return (
        // Set a minimum height to keep the Select from disappearing. Should be resolved by ARC-134
        // 144px is just the actual height from the Unit checkboxes
        <Box style={{ minHeight: '144px' }}>
            {props.isError ? (
                <Box mb={1}>
                    <InlineBanner
                        status="error"
                        title="Error Creating Queue"
                        description={errorOccurredWhile('creating a new queue')}
                    />
                </Box>
            ) : undefined}
            <Box width="450px">
                <BaseSelect
                    {...dataEntityTypeField}
                    isClearable={false}
                    options={[
                        { label: 'Events', value: 'CAD_AGENCY_EVENT' },
                        { label: 'Non-Dispatched Events (TRU)', value: 'TRU_EVENT' },
                        { label: 'Scheduled Events', value: 'SCHEDULED_AGENCY_EVENT' },
                        { label: 'Units', value: 'UNIT' },
                        ...(hospitalsEnabled ? [{ label: 'Hospitals', value: 'HOSPITAL' }] : []),
                        ...(viewBolos ? [{ label: 'BOLOs', value: 'BOLO' }] : []),
                    ]}
                />
            </Box>
            {!isCheckboxDisabled ? (
                <Grid templateColumns="1fr 1fr 1fr" gap="10" paddingTop="10px">
                    <CheckboxMultiselect
                        {...filtersField}
                        options={queueFilterOptions}
                        columns={Math.floor(queueFilterOptions.length / 3)}
                        w="450px"
                        disabledOptions={disabledOptions}
                    />
                </Grid>
            ) : null}
        </Box>
    );
}
